import {
  acessosDaCentralSchema,
  autoresAutorizadosSchema,
  detalheDaContaSchema,
  leituraDaContaSchema,
  leituraDaIaSchema,
  visaoGeralSchema,
  type AcessoDaCentral,
  type DetalheDaConta,
  type LeituraDaConta,
  type LeituraDaIa,
  type VisaoGeral,
} from "@base/contracts";
import { z } from "zod";
import type { ClienteHttp } from "../../../shared/http/cliente-http";
import type { CampanhaDaLeitura, CentralGateway, CorpoDasMetas } from "./central.gateway";

const semCorpo = z.null();
/** A API devolve o alerta atualizado; a tela so precisa saber que deu certo e busca a visao de novo. */
const qualquerCorpo = z.unknown();

/**
 * Central real. O `ClienteHttp` já prefixa `/api` e já manda `Idempotency-Key` na escrita.
 * - `GET /v1/central/visao-geral`
 * - `GET /v1/central/contas?cliente=&campanha=`
 * - `PUT /v1/central/contas/metas`
 * - `PUT /v1/central/contas/tipo`
 * - `POST /v1/central/alertas/:id/visto`
 * - `POST /v1/central/alertas/:id/resolver`
 * - `GET /v1/central/acessos`
 * - `GET /v1/central/autores`
 * - `PUT /v1/central/autores`
 * - `GET|POST /v1/central/contas/leitura-ia`
 */
export class CentralGatewayHttp implements CentralGateway {
  constructor(private readonly http: ClienteHttp) {}

  visaoGeral(periodo: "dia" | "semana" | "mes" = "semana"): Promise<VisaoGeral> {
    return this.http.requisitar("/v1/central/visao-geral", visaoGeralSchema, {
      consulta: { periodo: periodo === "semana" ? undefined : periodo },
    });
  }

  detalheDaConta(cliente: string, campanha: string, tipo = ""): Promise<DetalheDaConta> {
    return this.http.requisitar("/v1/central/contas", detalheDaContaSchema, {
      consulta: { cliente, campanha, tipo: tipo || undefined },
    });
  }

  leituraDaIa(campanha: CampanhaDaLeitura): Promise<LeituraDaConta> {
    return this.http.requisitar("/v1/central/contas/leitura-ia", leituraDaContaSchema, { consulta: { ...campanha } });
  }

  gerarLeituraDaIa(cliente: string, campanha: string): Promise<LeituraDaIa> {
    return this.http.requisitar("/v1/central/contas/leitura-ia", leituraDaIaSchema, {
      metodo: "POST",
      corpo: { cliente, campanha },
    });
  }

  async corrigirTipo(campanha: CampanhaDaLeitura, tipoCodigo: string): Promise<void> {
    await this.http.requisitar("/v1/central/contas/tipo", semCorpo, { metodo: "PUT", corpo: { ...campanha, tipoCodigo } });
  }

  async salvarMetas(corpo: CorpoDasMetas): Promise<void> {
    await this.http.requisitar("/v1/central/contas/metas", semCorpo, { metodo: "PUT", corpo });
  }

  async marcarAlertaComoVisto(id: string): Promise<void> {
    await this.http.requisitar(caminhoDoAlerta(id, "visto"), qualquerCorpo, { metodo: "POST" });
  }

  async resolverAlerta(id: string, nota: string): Promise<void> {
    await this.http.requisitar(caminhoDoAlerta(id, "resolver"), qualquerCorpo, {
      metodo: "POST",
      corpo: { nota },
    });
  }

  async acessos(): Promise<AcessoDaCentral[]> {
    const corpo = await this.http.requisitar("/v1/central/acessos", acessosDaCentralSchema);
    return corpo.acessos;
  }

  async autores(): Promise<string[]> {
    const corpo = await this.http.requisitar("/v1/central/autores", autoresAutorizadosSchema);
    return corpo.autores;
  }

  async salvarAutores(autores: string[]): Promise<void> {
    await this.http.requisitar("/v1/central/autores", qualquerCorpo, {
      metodo: "PUT",
      corpo: { autores },
    });
  }
}

function caminhoDoAlerta(id: string, acao: "visto" | "resolver"): string {
  return `/v1/central/alertas/${encodeURIComponent(id)}/${acao}`;
}
